/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	FxIdDomainEntitiesDocumentsDocumentType,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesAuthAuthenticateFromSocialRequest,
	FxIdWebFeaturesAuthAuthenticateFromSocialResponse,
	FxIdWebFeaturesFriendsGetFriendsResponse
} from "@app/shared/api/gen";
import { OS } from "@mantine/hooks";

export interface IFxIdSdkAdapterOpenModalRequestDto {
	Url: string;
}

export interface IFxIdSdkAdapterOpenModalResultDto {
	Success: boolean;
}

export interface IFxIdSdkAdapterPushTokenResponseDto {
	Token?: string;
}

export type IFxIdSdkAdapterEmptyDto = unknown;

export enum FxIdSdkAdapterStickyBannerShowErrorReason {
	Unknown = 0,
	AdsNotConnected = 1,
	NotImplemented = 999
}

export interface IFxIdSdkSaveToClipboardRequest {
	/**
	 * Текст для буфера обмена
	 */
	data: string;
	/**
	 * Если буфер обмена не доступен, то если тут стоит true будет отображена модалка из которой пользователь
	 * сможет скопировать данные сам
	 */
	showModalOnFail?: boolean;
}

export interface IFxIdSdkSaveToClipboardResponse {
	/**
	 * Удалось ли скопировать данные в буфер с помощью апи браузера
	 */
	savedToClipboard: boolean;
}

export interface IFxIdSdkAdapterStickyBannerShowResultDto {
	success: boolean;
	errorReason?: FxIdSdkAdapterStickyBannerShowErrorReason;
}

export interface IFxIdSdkGetUrlHashDto {
	hash: string;
}

export interface IFxIdSdkSetUrlHashDto {
	hash: string;
}

export interface IFxIdSdkShowLinkShareModalDto {
	Link: string;
}

export interface IFxIdSdkSetStatusDto {
	name: string;
	status: string;
	priority: number;
}

export interface ISafeArea {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

export interface IFxIdSdkAdapterInfo {
	/**
	 * Игра
	 */
	game: string;
	/**
	 * Данные о SDK
	 */
	sdk: {
		/**
		 * Версия сдк
		 */
		version: {
			major: number;
			minor: number;
		};
		/**
		 * Версия билда системы
		 */
		buildNumber: number;
	};
	/**
	 * Окружение игры
	 */
	environment: {
		/**
		 * Текущая OS
		 * 'undetermined' | 'macos' | 'ios' | 'windows' | 'android' | 'linux' | 'chromeos'
		 */
		os: OS;
		/**
		 * true если на мобилках
		 */
		isMobile: boolean;
		/**
		 * Сейф зоны, которые надо использовать
		 */
		safeArea: ISafeArea;
	};
	/**
	 * Данные аккаунта FxId
	 */
	fxId: {
		/**
		 * Идентификатор пользоватея
		 */
		userId: string;
		/**
		 * Разрешил ли пользователь слать ему маркетинговые материалы
		 */
		marketingEnabled: boolean;
		/**
		 * Локаль пользователя в FxId
		 */
		locale: string;
		/**
		 * Страна Alpha2 по Iso3166
		 */
		geoCountry: string;
		/**
		 * Токен для доступа к апи FxId
		 */
		token: string;
		/**
		 * Авторизационный токен в игру
		 */
		gameToken: string;
		/**
		 * Идентификатор пользователя в игре
		 */
		gameUid: string;
		/**
		 * true если пользователь гость
		 */
		guest: boolean;
		/**
		 * true если пользователь добавил емейл
		 */
		hasEmail: boolean;
		/**
		 * true если емейл провалидирован
		 */
		emailValidated: boolean;
		/**
		 * true если пользователь совершал платежи
		 */
		hasCompletedOrder: boolean;
	};
	/**
	 * Данные социальной сети
	 */
	social: IFxIdSdkAdapterInfoSocial;
}

export enum EFxIdSdkAdapterInfoSocialCapability {
	/**
	 * Поддерживается список друзей
	 */
	FriendsList = 0,
	/**
	 * Поддерживается приглашение друзей
	 */
	FriendsInvite = 1,
	/**
	 * Необходимо отключать игры с случайным исходом (рулетка итд)
	 */
	GamesOfChanceDisabled = 2,
	/**
	 * Можно ли показывать дилог с предложением поставить оценку
	 */
	RateApplication = 3,
	/**
	 * Нельзя указывать ссылки на другие платформы
	 */
	OutgoingLinksForbidden = 4,
	/**
	 * Доступна ли реклама на платформе
	 */
	AdsAvailable = 5
}

export type TSocialFlavor = "vkcom-direct";

export interface IFxIdSdkAdapterInfoSocial {
	/**
	 * Признак социальной сети
	 */
	social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform;
	/**
	 * Дополнительный признак для социльной сети
	 */
	socialFlavor?: TSocialFlavor;
	/**
	 * Идентификатор из социальной сети
	 */
	userId: string;
	/**
	 * Доступны ли платежи на платформе
	 */
	paymentsAvailable: boolean;
	/**
	 * Поддерживаемые возможности
	 */
	capabilities: EFxIdSdkAdapterInfoSocialCapability[];

	/**
	 * Опциональные данные - доступны не везде и не на всех платформах
	 */
	displayName?: string;
	firstName?: string;
	lastName?: string;
	gender?: EFxIdSdkGender;
	/**
	 * Некоторые платформы предоставляют данные о размере аватар, некоторые нет.
	 * Некоторые предоставляют дополнительную информацию о аватаре, некоторые нет
	 */
	photo?: {
		width?: number;
		height?: number;
		/**
		 * Ссылка на фотографию/аватар
		 */
		url: string;
	};
	/**
	 * Некоторые платформы позволяют установить свой язык пользователя. Вы должны реагировать на ChangeLanguage
	 */
	socialLocale?: string;
	/**
	 * Данные по соацильной сети VkontakteCom
	 */
	vkcom?: IFxIdSdkAdapterInfoSocialVkCom;
}

export interface IFxIdSdkAdapterInfoSocialVkCom {
	appId: number;
	defaultApiVersion: string;
	defaultAccessToken: string;
}

export interface IFxIdSdkAdapterBuyProductRequest {
	/**
	 * SKU товара для покупки
	 */
	sku: string;
	/**
	 * Произвольная строчка которая будет передана на сервер партнера после акцепта платежа
	 */
	developerPayload?: string;
	/**
	 * Название покупаемого товара.
	 * По возможности сервер возьмет эту строчку если ее надо будет показаться пользователю во время платежа
	 */
	productNameHint?: string;
	/**
	 * Описание покупаемого товара.
	 * По возможности сервер возьмет эту строчку если ее надо будет показаться пользователю во время платежа
	 */
	productDescriptionHint?: string;
}

export interface IFxIdSdkAdapterStatInitializeRequest {
	userId: string;
	userLevel: number;
	buildVersion: string;
	trackingEnabled?: boolean;
}

export interface IFxIdSdkAdapterStatEventRequest {
	name: string;
	type: string;
	subtype: string;
	section: string;
	value: number;
	data?: string;
}

export interface IFxIdSdkAdapterChangeLanguageRequest {
	lang: string;
}

export interface IFxIdSendVkBridgeRequest {
	method: string;
	props: any;
}

export interface IFxIdSendInGameEventRequest {
	game: string,
	eventCode: string,
	value: number,
	p1?: string,
	p2?: string,
	p3?: string
}

export interface IFxIdSendCrazyGamesSdkRequest {
	method: string;
	props?: any;
}

export interface IFxIdSdkAdapterBuyProductResponse {
	error?: string;
	transactionId?: string;
	errorCode?: EFxIdSdkAdapterBuyProductResponseErrorCode;
	stats?: {
		currency: string;
		currencyAmount: number;
		usdAmount: number;
	};
}

export enum EFxIdSdkAdapterBuyProductResponseErrorCode {
	Unknown = 0,
	UserCancelled = 1
}

export const FxIdSdkAdapterSocialSettingDefault: IFxIdSdkAdapterSocialSettings = {
	ui: {
		disabled: true
	}
};

export interface IFxIdSdkAdapterSocialSettings {
	ui: {
		// Если тру, то не показываем оформление совсем
		disabled: boolean;
	};
}

export interface IFxIdSdkAuthenticateApiResult {
	authResponse: FxIdWebFeaturesAuthAuthenticateFromSocialResponse;
	changeEvent?: IFxIdSdkOnAccountChangedEvent;
	changeInfoEvent?: IFxIdSdkOnChangeUserInfoEvent;
}

export interface IFxIdSdkOnAccountChangedEvent {
	oldAccountId: number;
	newAccountId: number;
}

export enum EFxIdSdkFocusChangedReason {
	Hint = "hint",
	Pip = "pip",
	Other = "other"
}

export interface IFxIdSdkRatingResponse {
	isRatingAvailable: boolean;
	rating?: number;
	created?: Date;
	comment?: string;
	platform?: string;
	success?: boolean;
}

export interface IFxIdSdkOnFocusChangedEvent {
	focus: boolean;
	reason?: EFxIdSdkFocusChangedReason;
	hint?: string;
}

export interface IFxIdSdkOnChangeUserInfoEvent {
	newInfo: IFxIdSdkAdapterInfo;
}

export interface IFxIdSdkOnChangeLanguageEvent {
	language: string;
}

export interface IFxIdSdkAuthenticateUser {
	preferredSocials?: string[];
	force?: {
		providerId: string;
	};
	import?: {
		socialIdentifier: string;
		socialIdentifierType: string;
	};
}

export enum EFxIdSdkGender {
	Unspecified = 0,
	Female,
	Male = 2
}

export interface IFxIdSdkGetFriendsResultFriend {
	firstName?: string;
	lastName?: string;
	displayName?: string;
	uid: string;
	gender?: EFxIdSdkGender;
	photo?: {
		width?: number;
		height?: number;
		url: string;
	};
}

export type IFxIdSdkAdapterGetFriendsRequest = unknown;

export interface IFxIdSdkAdapterInviteFriendsRequest {
	inviteText: string;
	base64image?: string;
	trackString?: string;
	gif?: { url: string };
	video?: { url: string };
}

export type IFxIdSdkAdapterAuthenticateUserRequest = unknown;

export type IFxIdSdkAdapterAuthenticateUserResponse = unknown;

export interface IFxIdSdkGetFriendsResult {
	friends: IFxIdSdkGetFriendsResultFriend[];
}

export interface IFxIdSdkInviteFriendsResult {
	friends: IFxIdSdkGetFriendsResultFriend[];
}

export interface IFxIdSdkGetFriendsResultForSdk extends FxIdWebFeaturesFriendsGetFriendsResponse {
	socialFriends: IFxIdSdkGetFriendsResultFriend[];
}

export interface IExportedFxIdSdkMethod {
	AuthenticateUser(requestJson: string): Promise<void>;

	AuthenticateApi(request: FxIdWebFeaturesAuthAuthenticateFromSocialRequest): Promise<IFxIdSdkAuthenticateApiResult>;

	IsAuthenticationApiInProgress(): boolean;
	IsMobile(): boolean;

	DispatchAdsFinished(): void;

	DispatchAdsSkipped(): void;

	DispatchAdsFailed(): void;
	DispatchAdsVideoAvailable(): void;
	DispatchDisableKeyboardInputCapture(): void;
	DispatchEnableKeyboardInputCapture(): void;
	DispatchAdsInterstitialAvailable(): void;

	DispatchFocusChanged(data: IFxIdSdkOnFocusChangedEvent): void;
}

export interface IFxIdJoinCommunityRequest {
	community_id: string;
}

export interface IFxIdJoinCommunityResult {
	success: boolean;
}

export type IFxIdAddToFavoritesRequest = unknown;

export interface IFxIdAddToFavoritesResult {
	success: boolean;
}

export interface IFxIdSdkWordsFilterRequest {
	text: string;
}

export interface IFxIdSdkWordsFilterResult {
	success: boolean;
}

export type IFxIdSdkRateApplicationRequest = unknown;

export interface IFxIdSdkRateApplicationResult {
	success: boolean;
}

export type IFxIdSdkSendFxEventResult = unknown;

export interface IFxIdSdkAdapterConsentManagerStatus {
	consentExists: boolean;
	consentData: any;
	cmpData: any;
}

export interface IFxIdSdkAdapterCanReviewResult {
	canReview: boolean;
}

export interface IFxIdSdkAdapterRequestReviewResult {
	shown: boolean;
}

export interface IFxIdSdkDocumentAccountAcceptResponseDto {
	Id: number;
	AccountId: number;
	DocumentVersionId: number;
	AcceptedAt: Date;
}

export interface IFxIdSdkVersionResponseDto {
	Id: number;
	Url: string;
	CreatedAt: Date;
	DocumentId: number;
}

export interface IFxIdSdkDocumentResponseDto {
	Id: number;
	Type: FxIdDomainEntitiesDocumentsDocumentType;
	CreatedAt: Date;
	IsActive: boolean;
	GameSystemName: string;
	Versions: Array<IFxIdSdkVersionResponseDto>;
}
